<script setup lang="ts">
import type { Property } from '@/core/entities/property';
import type { SectionName } from '~/types/mixpanel';
import type { H3ClusterId } from '~~/core/ports/repositories';

import { cn } from '@/utils';
import { EventBusKeys } from '~/core/helpers/symbols';

import PropertyListShortsSection from '../PropertyList/PropertyListShortsSection.vue';

const props = withDefaults(
	defineProps<{
		properties: Property[];
		showCardActions?: boolean;
		section?: SectionName;
		shouldShowShortsSection?: boolean;
		shortsInfoLabel?: string | null;
	}>(),
	{
		showCardActions: false,
		section: 'listed properties',
		shouldShowShortsSection: false,
		shortsInfoLabel: null
	}
);

const shouldRenderShortsSection = computed(
	() => props.shouldShowShortsSection && props.properties.length > 0
);

const shortsSectionClasses = computed(
	() => 'col-span-full order-[25] md:order-[45]'
);

function getPropertyOrder(index: number): number {
	return (index + 1) * 10;
}

const propertyCardHover = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardHover
);
const propertyCardUnhover = useEventBus<H3ClusterId[]>(
	EventBusKeys.propertyCardUnhover
);

function handlePropertyCardMouseEnter(property: Property) {
	if (!property.h3ClusterIds?.length) return;

	propertyCardHover.emit(property.h3ClusterIds);
}

function handlePropertyCardMouseLeave(property: Property) {
	if (!property.h3ClusterIds?.length) return;

	propertyCardUnhover.emit(property.h3ClusterIds);
}
</script>

<template>
	<PropertyCard
		v-for="(property, index) in properties"
		:key="property.id"
		variant="whiteBackground"
		:style="{ order: getPropertyOrder(index) }"
		:class="
			cn('cursor-pointer h-[340px] md:h-[378px]', {
				'mb-[30px]': index !== 0 && property.poiDistanceTime
			})
		"
		:property="property"
		:section="section"
		:show-actions="showCardActions"
		:card-position="index"
		:as-tags="{
			addresses: 'h3',
			askingPrice: 'h4',
			infoItems: 'h5'
		}"
		@mouseenter="handlePropertyCardMouseEnter(property)"
		@mouseleave="handlePropertyCardMouseLeave(property)"
	/>

	<PropertyListShortsSection
		v-if="shouldRenderShortsSection"
		:class="shortsSectionClasses"
		:info-label="props.shortsInfoLabel"
	/>
</template>
